import React, { useState } from 'react';
import '../styles/Modals.css';

// Server Modal
export function ServerModal({ server, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: server?.name || '',
    ip: server?.ip || '',
    username: server?.username || '',
    os_version: server?.os_version || '',
    node_version: server?.node_version || '',
    mongodb_name: server?.mongodb_name || '',
    mongodb_pass: server?.mongodb_pass || '',
    data_drive_size: server?.data_drive_size || '',
    cpu: server?.cpu || '',
    memory: server?.memory || '',
    autogenerated_backup_folders: server?.autogenerated_backup_folders?.join('\n') || '',
    backup_folders: server?.backup_folders?.join('\n') || ''
  });

  // Helper to generate the two default paths
  const getDefaultPaths = (user) => {
    if (!user) return '';
    return `/home/${user}/.ssh\n/home/${user}/important_files\n/home/${user}/shared_folder_between_apps\n/home/${user}/.pm2/logs\n/home/${user}/.pm2/dump.pm2`;
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    let newBackupFolders = formData.autogenerated_backup_folders;

    // Logic: If backup_folders is currently empty or ONLY contains 
    // the previous default paths, we auto-update it.
    // This prevents overwriting custom paths the user might have added.
    const currentPaths = formData.autogenerated_backup_folders.trim();
    const oldDefaultPaths = getDefaultPaths(formData.username).trim();
    
    if (currentPaths === '' || currentPaths === oldDefaultPaths) {
      
      newBackupFolders = getDefaultPaths(newUsername);
    }else {
      
      newBackupFolders = newBackupFolders + oldDefaultPaths
    }
    
    setFormData({
      ...formData,
      username: newUsername,
      autogenerated_backup_folders: newBackupFolders
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      backup_folders: formData.backup_folders.split('\n').filter(f => f.trim()),
      autogenerated_backup_folders: formData.autogenerated_backup_folders.split('\n').filter(f => f.trim())
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{server ? 'Edit Server' : 'Add Server'}</h2>
        <form onSubmit={handleSubmit}>
          <label>Server Name *</label>
          <input
            placeholder="Server Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <label>IP Address *</label>
          <input
            placeholder="IP Address *"
            value={formData.ip}
            onChange={(e) => setFormData({ ...formData, ip: e.target.value })}
            required
          />
          <label>Username *</label>

          <input
            placeholder="Username *"
            value={formData.username}
            onChange={handleUsernameChange} // Use the specialized handler
            required
          />
          <label>AutoGenerated Backup Folders</label>
          <textarea
            placeholder="AutoGenerated Folders (one per line)"
            value={formData.autogenerated_backup_folders}
            onChange={(e) => setFormData({ ...formData, autogenerated_backup_folders: e.target.value })}
            rows={6} // Increased rows to accommodate the new lines
          />
          <label>Backup Folders (one per line)</label>
          <textarea
            placeholder="Backup Folders (one per line)"
            value={formData.backup_folders}
            onChange={(e) => setFormData({ ...formData, backup_folders: e.target.value })}
            rows={6} // Increased rows to accommodate the new lines
          />
          <label>MongoDB Name</label>
          <input
            placeholder="MongoDB Name"
            value={formData.mongodb_name}
            onChange={(e) => setFormData({ ...formData, mongodb_name: e.target.value })}
          />
          <label>MongoDB Pass</label>
          <input
            placeholder="MongoDB Pass"
            value={formData.mongodb_pass}
            onChange={(e) => setFormData({ ...formData, mongodb_pass: e.target.value })}
          />
          <input
            placeholder="OS Version"
            value={formData.os_version}
            onChange={(e) => setFormData({ ...formData, os_version: e.target.value })}
          />
          <input
            placeholder="Node Version"
            value={formData.node_version}
            onChange={(e) => setFormData({ ...formData, node_version: e.target.value })}
          />
          <input
            placeholder="Data Drive Size"
            value={formData.data_drive_size}
            onChange={(e) => setFormData({ ...formData, data_drive_size: e.target.value })}
          />
          <input
            placeholder="CPU"
            value={formData.cpu}
            onChange={(e) => setFormData({ ...formData, cpu: e.target.value })}
          />
          <input
            placeholder="Memory"
            value={formData.memory}
            onChange={(e) => setFormData({ ...formData, memory: e.target.value })}
          />
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary">Save</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// App Modal
export function AppModal({ app, onSave, onClose }) {
  const [formData, setFormData] = useState({
    server_id: app?.server_id || '',
    name: app?.name || '',
    git_repo: app?.git_repo || '',
    git_branch: app?.git_branch || 'master',
    mongodb_db_name: app?.mongodb_db_name || '',
    backup_folders: app?.backup_folders?.join('\n') || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      backup_folders: formData.backup_folders.split('\n').filter(f => f.trim())
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{app?._id ? 'Edit App' : 'Add App'}</h2>
        <form onSubmit={handleSubmit}>
          <input
            placeholder="PM2 App Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <input
            placeholder="Git Repository URL *"
            value={formData.git_repo}
            onChange={(e) => setFormData({ ...formData, git_repo: e.target.value })}
            required
          />
          <input
            placeholder="Git Branch *"
            value={formData.git_branch}
            onChange={(e) => setFormData({ ...formData, git_branch: e.target.value })}
            required
          />
          <input
            placeholder="MongoDB Database Name"
            value={formData.mongodb_db_name}
            onChange={(e) => setFormData({ ...formData, mongodb_db_name: e.target.value })}
          />
          <textarea
            placeholder="Backup Folders (one per line)"
            value={formData.backup_folders}
            onChange={(e) => setFormData({ ...formData, backup_folders: e.target.value })}
            rows={4}
          />
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary">Save</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Schedule Modal
export function ScheduleModal({ target, type, onSave, onClose }) {
  const [formData, setFormData] = useState({
    schedule_cron: target?.schedule_cron || '0 2 * * *',
    schedule_enabled: target?.schedule_enabled !== undefined ? target.schedule_enabled : false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Schedule Backup - {type === 'server' ? 'Server' : 'App'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.schedule_enabled}
                onChange={(e) => setFormData({ ...formData, schedule_enabled: e.target.checked })}
              />
              Enable Schedule
            </label>
          </div>
          <input
            placeholder="Cron Expression (e.g., 0 2 * * *)"
            value={formData.schedule_cron}
            onChange={(e) => setFormData({ ...formData, schedule_cron: e.target.value })}
            disabled={!formData.schedule_enabled}
          />
          <div className="cron-help">
            <p>Examples:</p>
            <ul>
              <li>0 2 * * * - Daily at 2:00 AM</li>
              <li>0 */6 * * * - Every 6 hours</li>
              <li>0 3 * * 0 - Weekly on Sunday at 3:00 AM</li>
            </ul>
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary">Save</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Deploy Modal
export function DeployModal({ target, type, onDeploy, onClose }) {
  const [formData, setFormData] = useState({
    new_server_ip: '',
    backup_to_restore: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onDeploy(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Deploy {type === 'server' ? 'Server' : 'App'}</h2>
        <form onSubmit={handleSubmit}>
          <input
            placeholder="New Server IP Address *"
            value={formData.new_server_ip}
            onChange={(e) => setFormData({ ...formData, new_server_ip: e.target.value })}
            required
          />
          <input
            placeholder="Backup S3 Key to Restore (optional)"
            value={formData.backup_to_restore}
            onChange={(e) => setFormData({ ...formData, backup_to_restore: e.target.value })}
          />
          <div className="modal-actions">
            <button type="submit" className="btn btn-primary">Start Deploy</button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Cancel Job Modal
export default function CancelJobModal({ job, onConfirm, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
        <h2>Cancel Job</h2>
        <p className="warning-text">Job is currently running. How would you like to cancel?</p>
        <div className="cancel-options">
          <button 
            className="btn btn-danger btn-block" 
            onClick={() => onConfirm('immediate')}
          >
            Stop Immediately
            <span className="btn-hint">Abort current step</span>
          </button>
          <button 
            className="btn btn-warning btn-block" 
            onClick={() => onConfirm('graceful')}
          >
            Finish Current Step
            <span className="btn-hint">Complete current, skip remaining</span>
          </button>
          <button 
            className="btn btn-secondary btn-block" 
            onClick={onClose}
          >
            Keep Running
          </button>
        </div>
      </div>
    </div>
  );
}

